/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import Download from './components/Download';
import AboutUs from './components/AboutUs';
import Footer from './components/Footer';
import LegalPage from './components/LegalPage';
import UserServices from './components/UserServices';
import SongSubmissionPc from './components/SongSubmissionPc';
import Domily from './components/Domily';
import Domi from './components/Domi';
import BandRecruitment from './components/BandRecruitment';
import { PageRoute } from './types';
import { PcUser, readPcUser, expirePcLogin, PC_LOGIN_EXPIRED_EVENT, PC_LOGIN_EXPIRED_MESSAGE } from './lib/pcApi';
import { privacyPolicy, userAgreement, commercialDisclosure } from './data/legalText';
import { trackGtmPageEnter } from './lib/gtm';

const PAGE_TITLES: Record<PageRoute, string> = {
  home: 'Koemori｜歌声で、もっとつながる',
  services: 'ユーザーサービス｜Koemori',
  'song-submission': '楽曲アップロード｜Koemori',
  domily: 'Domilyについて｜Koemori',
  domi: 'ドミについて｜Koemori',
  recruit: 'アーティスト登録｜Koemori',
  about: 'Koemoriについて｜Koemori',
  privacy: 'プライバシーポリシー｜Koemori',
  terms: '利用規約｜Koemori',
  commercial: '特定商取引法に基づく表記｜Koemori',
};

const legalRoutes: PageRoute[] = ['privacy', 'terms', 'commercial'];

export default function App() {
  const [currentRoute, setRoute] = useState<PageRoute>('home');
  const [routeBeforeLegal, setRouteBeforeLegal] = useState<PageRoute>('home');
  const [loginOpen, setLoginOpen] = useState(false);
  const [pcUser, setPcUser] = useState<PcUser | null>(readPcUser);
  const [loginNotice, setLoginNotice] = useState('');
  const isSectionScrolling = useRef(false);

  useEffect(() => {
    const onExpired = () => {
      setPcUser(null);
      setLoginNotice(PC_LOGIN_EXPIRED_MESSAGE);
      setLoginOpen(true);
    };
    window.addEventListener(PC_LOGIN_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(PC_LOGIN_EXPIRED_EVENT, onExpired);
  }, []);

  useEffect(() => {
    if (!pcUser) return;
    setLoginNotice('');
    const checkSession = () => { if (!readPcUser()) expirePcLogin(); };
    checkSession();
    const timer = window.setInterval(checkSession, 30_000);
    window.addEventListener('focus', checkSession);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', checkSession); };
  }, [pcUser]);

  useEffect(() => {
    document.title = PAGE_TITLES[currentRoute];
    trackGtmPageEnter({
      page_name: currentRoute,
      page_path: `/${currentRoute === 'home' ? '' : currentRoute}`,
      page_title: PAGE_TITLES[currentRoute],
    });
  }, [currentRoute]);

  useEffect(() => {

    const rootElement = document.documentElement;
    const previousSnapType = rootElement.style.scrollSnapType;
    if (currentRoute === 'about') {
      rootElement.style.scrollSnapType = 'y mandatory';
    }

    const sectionIdsByRoute: Record<PageRoute, string[]> = {
      home: ['hero', 'feature-home', 'feature-sing', 'feature-beautify', 'feature-intimacy', 'download'],
      services: ['services-page'],
      'song-submission': ['song-submission-page'],
      domily: ['domily-page'],
      domi: ['domi-page'],
      recruit: ['recruit-page'],
      about: ['about-intro', 'about-company'],
      privacy: ['legal-page'],
      terms: ['legal-page'],
      commercial: ['legal-page'],
    };
    const sectionIds = sectionIdsByRoute[currentRoute];
    let unlockTimer: number | undefined;

    const handleWheel = (event: WheelEvent) => {
      if (
        !window.matchMedia('(min-width: 1024px) and (min-height: 720px)').matches ||
        event.ctrlKey ||
        Math.abs(event.deltaY) < 8 ||
        Math.abs(event.deltaY) <= Math.abs(event.deltaX)
      ) {
        return;
      }

      if (isSectionScrolling.current) {
        event.preventDefault();
        return;
      }

      const sections = sectionIds
        .map((id) => document.getElementById(id))
        .filter((section): section is HTMLElement => Boolean(section));

      if (sections.length === 0) return;

      const navbarHeight = 72;
      const currentTop = window.scrollY;
      const currentIndex = sections.reduce((activeIndex, section, index) => {
        const snapTop = Math.max(0, section.offsetTop - navbarHeight);
        return currentTop >= snapTop - 4 ? index : activeIndex;
      }, 0);
      const currentSection = sections[currentIndex];
      const direction = event.deltaY > 0 ? 1 : -1;

      // The download section is taller than one screen. Let users reach the
      // application form before paging to the neighbouring section.
      if (currentSection.id === 'download') {
        const sectionTop = Math.max(0, currentSection.offsetTop - navbarHeight);
        const sectionEnd = Math.max(
          sectionTop,
          currentSection.offsetTop + currentSection.offsetHeight - window.innerHeight,
        );
        const edgeTolerance = 16;

        if (
          (direction > 0 && currentTop < sectionEnd - edgeTolerance) ||
          (direction < 0 && currentTop > sectionTop + edgeTolerance)
        ) {
          return;
        }
      }

      const targetIndex = Math.min(
        sections.length - 1,
        Math.max(0, currentIndex + direction),
      );

      if (targetIndex === currentIndex) return;

      event.preventDefault();
      isSectionScrolling.current = true;
      sections[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      unlockTimer = window.setTimeout(() => {
        isSectionScrolling.current = false;
      }, 850);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (unlockTimer !== undefined) window.clearTimeout(unlockTimer);
      isSectionScrolling.current = false;
      rootElement.style.scrollSnapType = previousSnapType;
    };
  }, [currentRoute]);
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navigateToRoute = (nextRoute: PageRoute) => {
    if (legalRoutes.includes(nextRoute) && !legalRoutes.includes(currentRoute)) {
      setRouteBeforeLegal(currentRoute);
    }
    setRoute(nextRoute);
  };

  const handleBackToHome = () => {
    setRoute(routeBeforeLegal);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#080A0A] text-white flex flex-col font-sans antialiased selection:bg-[#00F5D4]/20 selection:text-[#00F5D4] overflow-x-hidden">
      <Navbar
        currentRoute={currentRoute}
        setRoute={navigateToRoute}
        scrollToSection={scrollToSection}
        onBackFromLegal={handleBackToHome}
        loginOpen={loginOpen}
        loginNotice={loginNotice}
        setLoginOpen={setLoginOpen}
        pcUser={pcUser}
        setPcUser={setPcUser}
      />

      <main className={currentRoute === 'home' ? 'flex-grow' : 'flex-grow pt-11'}>
        {currentRoute === 'home' && (
          <div className="home-page">
            <Hero
              scrollToSection={scrollToSection}
            />
            <Features />
            <section id="download" className="min-h-[calc(100vh-72px)] bg-[#080A0A] flex flex-col">
              <Download />
              <div className="mt-auto">
                <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
              </div>
            </section>
          </div>
        )}

        {currentRoute === 'services' && (
          <div className="min-h-[calc(100vh-44px)] bg-[#080A0A] pt-[72px] flex flex-col">
            <UserServices setRoute={navigateToRoute} />
            <div className="mt-auto">
              <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
            </div>
          </div>
        )}

        {currentRoute === 'song-submission' && (
          <div className="min-h-[calc(100vh-44px)] bg-[#080A0A] flex flex-col">
            <SongSubmissionPc user={pcUser} onLoginRequested={() => setLoginOpen(true)} onRecruitRequested={() => navigateToRoute('recruit')} />
            <div className="mt-auto">
              <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
            </div>
          </div>
        )}

        {currentRoute === 'domily' && (
          <div className="min-h-[calc(100vh-44px)] bg-[#080A0A] pt-[72px] flex flex-col">
            <Domily setRoute={navigateToRoute} />
            <div className="mt-auto">
              <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
            </div>
          </div>
        )}

        {currentRoute === 'domi' && (
          <div className="min-h-[calc(100vh-44px)] bg-[#0A0A0A] pt-[72px] flex flex-col">
            <Domi setRoute={navigateToRoute} />
            <div className="mt-auto">
              <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
            </div>
          </div>
        )}

        {currentRoute === 'recruit' && (
          <div className="min-h-[calc(100vh-44px)] bg-[#080A0A] flex flex-col">
            <BandRecruitment user={pcUser} onLoginRequested={() => setLoginOpen(true)} />
            <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
          </div>
        )}
        {currentRoute === 'about' && (
          <div className="min-h-[calc(100vh-44px)] bg-[#0E1110] pt-[72px] flex flex-col">
            <AboutUs />
            <div id="about-footer" className="snap-start">
              <Footer setRoute={navigateToRoute} scrollToSection={scrollToSection} showSocials />
            </div>
          </div>
        )}

        {currentRoute === 'privacy' && (
          <LegalPage doc={privacyPolicy} onBack={handleBackToHome} />
        )}
        {currentRoute === 'terms' && (
          <LegalPage doc={userAgreement} onBack={handleBackToHome} />
        )}
        {currentRoute === 'commercial' && (
          <LegalPage doc={commercialDisclosure} onBack={handleBackToHome} />
        )}
      </main>
    </div>
  );
}
